/**
 * GoalContext
 * Global state management for goals and accounts
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Goal, GoalType } from '../models/Goal';
import { GoalAccount } from '../models/GoalAccount';
import {
  getAllAccounts,
  getAccountById,
  createAccount,
  updateAccount,
  deleteAccount,
  getCurrentAccountId,
  setCurrentAccountId,
  getGoalsByAccountId,
  getGoalById,
  getMainGoal,
  getSideGoals,
  createGoal,
  updateGoal,
  deleteGoal,
} from '../services/storage/GoalStorage';

interface GoalContextType {
  // Account state
  accounts: GoalAccount[];
  currentAccount: GoalAccount | null;
  currentAccountId: string | null;

  // Goals state
  goals: Goal[]; // All goals across all accounts
  mainGoal: Goal | null;
  sideGoals: Goal[];

  // Loading state
  isLoading: boolean;

  // Account operations
  createNewAccount: (name: string, themeColor?: string) => Promise<GoalAccount>;
  updateExistingAccount: (accountId: string, updates: Partial<Omit<GoalAccount, 'id' | 'createdAt'>>) => Promise<void>;
  deleteExistingAccount: (accountId: string) => Promise<void>;
  switchAccount: (accountId: string) => Promise<void>;

  // Goal operations
  createNewGoal: (name: string, type: GoalType, accountId?: string) => Promise<Goal>;
  updateExistingGoal: (goalId: string, updates: Partial<Omit<Goal, 'id' | 'accountId' | 'createdAt'>>) => Promise<void>;
  deleteExistingGoal: (goalId: string) => Promise<void>;

  // Utility
  refreshGoals: () => Promise<void>;
}

const GoalContext = createContext<GoalContextType | undefined>(undefined);

interface GoalProviderProps {
  children: ReactNode;
}

export const GoalProvider: React.FC<GoalProviderProps> = ({ children }) => {
  const [accounts, setAccounts] = useState<GoalAccount[]>([]);
  const [currentAccount, setCurrentAccount] = useState<GoalAccount | null>(null);
  const [currentAccountId, setCurrentAccountIdState] = useState<string | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]); // All goals across all accounts
  const [mainGoal, setMainGoal] = useState<Goal | null>(null);
  const [sideGoals, setSideGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load initial data on mount
   */
  useEffect(() => {
    loadInitialData();
  }, []);

  /**
   * Load goals when current account changes
   */
  useEffect(() => {
    if (currentAccountId) {
      loadGoalsForAccount(currentAccountId);
    }
  }, [currentAccountId]);

  const loadInitialData = async () => {
    try {
      console.log('=== GoalContext: Starting loadInitialData ===');
      setIsLoading(true);

      // Load all accounts
      console.log('GoalContext: Loading accounts...');
      let allAccounts = await getAllAccounts();
      console.log('GoalContext: Loaded accounts:', allAccounts.length);

      // Auto-create default account if none exists
      if (allAccounts.length === 0) {
        console.log('No accounts found - creating default account');
        const defaultAccount = await createAccount('My Goals');
        allAccounts = [defaultAccount];
        await setCurrentAccountId(defaultAccount.id);
      }

      setAccounts(allAccounts);

      // Load current account
      const accountId = await getCurrentAccountId();
      setCurrentAccountIdState(accountId);

      if (accountId) {
        const account = await getAccountById(accountId);
        setCurrentAccount(account);
      }

      // Load all goals across all accounts
      await refreshGoals();
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGoalsForAccount = async (accountId: string) => {
    try {
      // Filter goals for this account from the global goals array
      const accountGoals = goals.filter(g => g.accountId === accountId);

      const main = accountGoals.find(g => g.type === 'main') || null;
      setMainGoal(main);

      const side = accountGoals.filter(g => g.type === 'side');
      setSideGoals(side);
    } catch (error) {
      console.error('Failed to load goals:', error);
    }
  };

  const refreshGoals = async () => {
    try {
      // Load goals from all accounts
      const allAccounts = await getAllAccounts();
      const goalsPromises = allAccounts.map(account => getGoalsByAccountId(account.id));
      const goalsArrays = await Promise.all(goalsPromises);

      // Flatten into single array and set as global goals
      const combinedGoals = goalsArrays.flat();
      setGoals(combinedGoals);

      // Update current account goals if there's a current account
      if (currentAccountId) {
        const accountGoals = combinedGoals.filter(g => g.accountId === currentAccountId);
        const main = accountGoals.find(g => g.type === 'main') || null;
        setMainGoal(main);
        const side = accountGoals.filter(g => g.type === 'side');
        setSideGoals(side);
      }
    } catch (error) {
      console.error('Failed to refresh goals:', error);
    }
  };

  const createNewAccount = async (name: string, themeColor?: string): Promise<GoalAccount> => {
    const account = await createAccount(name, themeColor);

    // Refresh accounts list
    const allAccounts = await getAllAccounts();
    setAccounts(allAccounts);

    // If this is the first account, set it as current
    if (!currentAccountId) {
      setCurrentAccountIdState(account.id);
      setCurrentAccount(account);
    }

    return account;
  };

  const updateExistingAccount = async (
    accountId: string,
    updates: Partial<Omit<GoalAccount, 'id' | 'createdAt'>>
  ): Promise<void> => {
    await updateAccount(accountId, updates);

    // Refresh accounts list
    const allAccounts = await getAllAccounts();
    setAccounts(allAccounts);

    // Update current account if it was modified
    if (accountId === currentAccountId) {
      const account = await getAccountById(accountId);
      setCurrentAccount(account);
    }
  };

  const deleteExistingAccount = async (accountId: string): Promise<void> => {
    await deleteAccount(accountId);

    // Refresh accounts list
    const allAccounts = await getAllAccounts();
    setAccounts(allAccounts);

    // Update current account (storage layer handles switching)
    const newAccountId = await getCurrentAccountId();
    setCurrentAccountIdState(newAccountId);

    if (newAccountId) {
      const account = await getAccountById(newAccountId);
      setCurrentAccount(account);
    } else {
      setCurrentAccount(null);
      setGoals([]);
      setMainGoal(null);
      setSideGoals([]);
    }
  };

  const switchAccount = async (accountId: string): Promise<void> => {
    await setCurrentAccountId(accountId);
    setCurrentAccountIdState(accountId);

    const account = await getAccountById(accountId);
    setCurrentAccount(account);

    // Goals will be loaded by useEffect
  };

  const createNewGoal = async (name: string, type: GoalType, accountId?: string): Promise<Goal> => {
    const targetAccountId = accountId || currentAccountId;

    if (!targetAccountId) {
      throw new Error('No current account selected');
    }

    const goal = await createGoal(targetAccountId, name, type);

    // Refresh all goals
    await refreshGoals();

    return goal;
  };

  const updateExistingGoal = async (
    goalId: string,
    updates: Partial<Omit<Goal, 'id' | 'accountId' | 'createdAt'>>
  ): Promise<void> => {
    if (!currentAccountId) {
      throw new Error('No current account selected');
    }

    await updateGoal(currentAccountId, goalId, updates);

    // Refresh all goals
    await refreshGoals();
  };

  const deleteExistingGoal = async (goalId: string): Promise<void> => {
    if (!currentAccountId) {
      throw new Error('No current account selected');
    }

    await deleteGoal(currentAccountId, goalId);

    // Refresh all goals
    await refreshGoals();
  };

  const value: GoalContextType = {
    // State
    accounts,
    currentAccount,
    currentAccountId,
    goals,
    mainGoal,
    sideGoals,
    isLoading,

    // Account operations
    createNewAccount,
    updateExistingAccount,
    deleteExistingAccount,
    switchAccount,

    // Goal operations
    createNewGoal,
    updateExistingGoal,
    deleteExistingGoal,

    // Utility
    refreshGoals,
  };

  return <GoalContext.Provider value={value}>{children}</GoalContext.Provider>;
};

/**
 * Hook to use GoalContext
 */
export const useGoalContext = (): GoalContextType => {
  const context = useContext(GoalContext);
  if (!context) {
    throw new Error('useGoalContext must be used within a GoalProvider');
  }
  return context;
};
